/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ofType } from 'redux-observable';
import { from, of, combineLatest } from 'rxjs';
import { withLatestFrom, pluck, mergeMap, reduce, map, filter } from 'rxjs/operators';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { calendarDateToUtcString } from 'sarsaparilla';
import { internalAuthCampingAxios } from 'ui-internal-camping/src/utils/internalCampingAxios';
import { type CalendarDate, maxDate, startOfMonth } from '@internationalized/date';

import { urlWithParams } from '../../utils/utils';
import { campingAxios } from '../../utils/campingAxios';
import { MAX_RETRY_COUNT } from '../../constants/campground';
import {
    FETCH_MONTHLY_AVAILABILITY,
    SET_RATE_LIMITED_MONTHS,
    FETCH_MONTHLY_AVAILABILITY_SUCCESS,
    PAUSE_LOADING,
    NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX,
} from '../../constants';
import { getNewMonthsToFetch } from '../../utils/availabilityReducerUtil';
import { UTCStringToCalendarDate } from '../../utils/dateUtils';

function fetchCampgroundAvailabilityByMonth({
    startDate,
    campgroundId,
    isInternal,
}: {
    startDate: string;
    campgroundId: string;
    isInternal: boolean;
}) {
    // format the URL to be used in 'fetch'
    const request = urlWithParams(`/availability/campground/${campgroundId}/month`, {
        start_date: startDate,
    });

    const axiosInstance = isInternal ? internalAuthCampingAxios : campingAxios;

    return axiosInstance.get(request).catch((err) => {
        const isRateLimited = get(err, 'response.status') === 429;

        return {
            isRateLimited,
            error: err,
        };
    });
}

const transformToPromise = (
    startDate: string,
    campgroundId: string,
    isInternal: boolean
) => {
    return from(
        fetchCampgroundAvailabilityByMonth({
            startDate,
            campgroundId,
            isInternal,
        })
    ).pipe(
        map((response) => ({
            response,
            month: startDate,
        }))
    );
};

/**
 *
 * @param {Observable} action$ - a stream of actions
 * @param {Observable} state$ - a stream of redux store states
 */
export const fetchMonthlyAvailabilityEpic = (action$, state$) =>
    action$.pipe(
        ofType(FETCH_MONTHLY_AVAILABILITY),
        withLatestFrom(
            combineLatest(
                state$.pipe(pluck('campground'), pluck('campground'), pluck('results')),
                state$.pipe(pluck('availabilityGrid'))
            )
        ),
        /* When useMonthlyAvailability owns fetching, this epic stands down. Callers still
        dispatch FETCH_MONTHLY_AVAILABILITY, which drives isLoading in the reducer. */
        filter(
            ([, [, availabilityGrid]]) => !availabilityGrid?.isAvailabilityQueryEnabled
        ),
        mergeMap(([input, state]) => {
            const { count = 0, payload = {}, monthsFetched = {}, monthsToFetch } = input;
            const [campground, availabilityGrid] = state;
            const {
                startDate,
                endDate,
                shouldRefresh,
                facilityId,
                isInternal,
                isModify,
            } = payload;
            const campgroundId = campground.facility_id || facilityId;
            const { availabilitiesByMonth } = availabilityGrid;
            const gridEndDate = UTCStringToCalendarDate(startDate).add({
                days: NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX,
            });
            const startDateString = calendarDateToUtcString(
                startOfMonth(UTCStringToCalendarDate(startDate) as CalendarDate)
            );
            const endDateToFetch = endDate
                ? maxDate(UTCStringToCalendarDate(endDate), gridEndDate)
                : gridEndDate;
            const endDateString = calendarDateToUtcString(startOfMonth(endDateToFetch));

            // TODO check season data to prevent fetching outside of the season
            const newMonthsToFetch =
                monthsToFetch ||
                getNewMonthsToFetch({
                    availabilitiesByMonth,
                    startDate: startDateString!,
                    endDate: endDateString!,
                    shouldRefresh,
                    isInternal,
                });

            if (!campground.facility_id || isEmpty(newMonthsToFetch)) {
                return of({
                    type: PAUSE_LOADING,
                });
            }

            return of(...newMonthsToFetch).pipe(
                mergeMap((month) => transformToPromise(month, campgroundId, isInternal)),
                reduce(
                    (acc, curr) => {
                        const {
                            data,
                            isRateLimited,
                            error,
                        }: { data: any; isRateLimited: boolean; error: any } =
                            curr.response;
                        const month: string = curr.month;
                        if (isRateLimited) {
                            acc.rateLimited.push(month);
                        }

                        if (error) {
                            acc.failure.push(month);
                        }

                        if (data?.campsites) {
                            acc.success[month] = data.campsites;
                        }

                        return acc;
                    },
                    { success: {}, failure: [], rateLimited: [] }
                ),
                mergeMap((responses) => {
                    const actions = [];
                    const { success, failure, rateLimited } = responses;

                    if (!isEmpty(rateLimited) || !isEmpty(failure)) {
                        const monthsToRetry = [...rateLimited, ...failure];
                        if (count <= MAX_RETRY_COUNT) {
                            return of({
                                type: FETCH_MONTHLY_AVAILABILITY,
                                monthsToFetch: monthsToRetry,
                                monthsFetched: success,
                                count: count + 1,
                                facilityId,
                                payload,
                            });
                        }
                        if (count === MAX_RETRY_COUNT + 1) {
                            actions.push({
                                type: SET_RATE_LIMITED_MONTHS,
                                months: rateLimited,
                            });
                        }
                    } else if (!isEmpty(success)) {
                        actions.push({
                            type: FETCH_MONTHLY_AVAILABILITY_SUCCESS,
                            monthData: { ...success, ...monthsFetched },
                            isModify,
                        });
                    }

                    return of.apply(this, actions);
                })
            );
        })
    );
